import { z } from 'zod';

import { createPaginatedResponseSchema } from './common';

const OrganizationPathItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

export const CompanySchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  status: z.string(),
  organization_id: z.string().uuid().nullable().optional(),
  organization_path: z.array(OrganizationPathItemSchema).nullable().optional(),
  customers_count: z.number().int().nonnegative().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime().optional(),
});

export const CompaniesResponseSchema = z.array(CompanySchema);

export const PaginatedCompaniesResponseSchema = createPaginatedResponseSchema(CompanySchema);

export const CreateCompanyRequestSchema = z.object({
  name: z.string().min(1),
  status: z.string(),
  organization_id: z.string().uuid().nullable().optional(),
});

export const CreateCompanyResponseSchema = CompanySchema;

export const CompanyUserRoleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  is_system: z.boolean().default(false),
});

export const CompanyCustomerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  surname: z.string(),
  full_name: z.string(),
  email: z.string().email(),
  is_owner: z.boolean().optional(),
  roles: z.array(CompanyUserRoleSchema).default([]),
});

export const CompanyCustomersResponseSchema = z.array(CompanyCustomerSchema);

export const PaginationMetaSchema = z.object({
  current_page: z.number().int().positive(),
  last_page: z.number().int().positive(),
  per_page: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  from: z.number().int().positive().nullable(),
  to: z.number().int().positive().nullable(),
});

export const PaginatedCustomersResponseSchema = createPaginatedResponseSchema(CompanyCustomerSchema);

export const AttachCustomerRequestSchema = z.object({
  customer_id: z.string().uuid(),
  is_owner: z.boolean(),
  role_ids: z.array(z.string().uuid()).default([]),
});

export const AssignableRoleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  label: z.string(),
  is_system: z.boolean(),
  permissions_count: z.number().int().nonnegative(),
  users_count: z.number().int().nonnegative().default(0),
  permissions: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    )
    .default([]),
});

export const AssignableRolesResponseSchema = z.object({
  items: z.array(AssignableRoleSchema),
});

export const UpdateCustomerRoleRequestSchema = z.object({
  is_owner: z.boolean(),
});

export const AttachCustomerResponseSchema = CompanyCustomerSchema.extend({
  message: z.string(),
});

export const UserActivityLogSchema = z.object({
  id: z.string().uuid(),
  log_name: z.string().nullable(),
  description: z.string(),
  subject_type: z.string().nullable(),
  subject_id: z.number().nullable(),
  causer_type: z.string().nullable(),
  causer_id: z.number().nullable(),
  properties: z
    .object({
      attributes: z.record(z.string(), z.unknown()).optional(),
      old: z.record(z.string(), z.unknown()).optional(),
    })
    .nullable(),
  event: z.string().nullable(),
  batch_id: z.string().nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime().optional(),
});

export const ActivityLogSchema = z.object({
  log_name: z.string(),
  description: z.string(),
  subject_type_name: z.string().nullable(),
  subject_name: z.string().nullable(),
  causer_name: z.string(),
  properties: z.record(z.string(), z.any()),
  created_at: z.string().datetime(),
});

export const PaginatedUserActivityLogsResponseSchema = createPaginatedResponseSchema(UserActivityLogSchema);

export const PaginatedActivityLogsResponseSchema = createPaginatedResponseSchema(ActivityLogSchema);

export const CompanyInvitationSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  status: z.enum(['pending', 'accepted', 'cancelled', 'rejected', 'expired']),
  created_at: z.string().datetime(),
  accepted_at: z.string().datetime().nullable().optional(),
  rejected_at: z.string().datetime().nullable().optional(),
  rejection_reason: z.string().nullable().optional(),
  is_owner: z.boolean().default(false),
  roles: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
  invited_by: z
    .object({
      name: z.string(),
      email: z.string().email(),
      type: z.enum(['employee', 'customer', 'unknown']).optional(),
    })
    .nullable()
    .optional(),
});

export const CompanyInvitationsResponseSchema = z.array(CompanyInvitationSchema);

export const CreateInvitationRequestSchema = z.object({
  email: z.string().email().optional(),
  customer_id: z.string().uuid().optional(),
  is_owner: z.boolean(),
  role_ids: z.array(z.string().uuid()).default([]),
});

export const CompanyPermissionsResponseSchema = z.object({
  permissions: z.array(z.string()),
  roles: z.array(z.string()),
  is_owner: z.boolean(),
});

const PermissionItemSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const CompanyMemberDetailSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
  is_owner: z.boolean(),
  roles: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        permissions: z.array(PermissionItemSchema).default([]),
      }),
    )
    .default([]),
  permissions: z.array(PermissionItemSchema.extend({ guard_name: z.string().optional() })).default([]),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
});

export type Company = z.infer<typeof CompanySchema>;
export type CompaniesResponse = z.infer<typeof CompaniesResponseSchema>;
export type PaginatedCompaniesResponse = z.infer<typeof PaginatedCompaniesResponseSchema>;
export type CreateCompanyRequest = z.infer<typeof CreateCompanyRequestSchema>;
export type CreateCompanyResponse = z.infer<typeof CreateCompanyResponseSchema>;
export type CompanyCustomer = z.infer<typeof CompanyCustomerSchema>;
export type CompanyCustomersResponse = z.infer<typeof CompanyCustomersResponseSchema>;
export type PaginatedCustomersResponse = z.infer<typeof PaginatedCustomersResponseSchema>;
export type AttachCustomerRequest = z.infer<typeof AttachCustomerRequestSchema>;
export type UpdateCustomerRoleRequest = z.infer<typeof UpdateCustomerRoleRequestSchema>;
export type UserActivityLog = z.infer<typeof UserActivityLogSchema>;
export type ActivityLog = z.infer<typeof ActivityLogSchema>;
export type PaginatedUserActivityLogsResponse = z.infer<typeof PaginatedUserActivityLogsResponseSchema>;
export type PaginatedActivityLogsResponse = z.infer<typeof PaginatedActivityLogsResponseSchema>;
export type CompanyInvitation = z.infer<typeof CompanyInvitationSchema>;
export type CreateInvitationRequest = z.infer<typeof CreateInvitationRequestSchema>;
export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;
export type CompanyPermissionsResponse = z.infer<typeof CompanyPermissionsResponseSchema>;
export type AssignableRole = z.infer<typeof AssignableRoleSchema>;
export type AssignableRolesResponse = z.infer<typeof AssignableRolesResponseSchema>;
export type CompanyUserRole = z.infer<typeof CompanyUserRoleSchema>;
export type CompanyMemberDetail = z.infer<typeof CompanyMemberDetailSchema>;
